'use client';

import { useLocale, useTranslations } from 'next-intl';
import { getZodiacSignFromDate } from '@repo/lib';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import Loader from '@/components/loader';
import { useUser } from '@/contexts/user-context';
import LandingExperience from '@/features/landing/landing';
import type { OnboardingData } from '@/features/onboarding/onboarding';
import type { AcquisitionIntent } from '@/lib/acquisition-intent';
import { markOfferPending } from '@/lib/offer-gate';
import { GUEST_ONBOARDING_SCHEMA_VERSION, useGuestStore } from '@/lib/stores/guest-store';

type Status =
  | 'loading'
  | 'redirecting'
  | 'authenticated'
  | 'guest'
  | 'onboarding'
  | 'transitioning'
  | 'error';

type UseOnboardingRedirectReturn = {
  status: Status;
  component: React.ReactNode;
};

const TRANSITION_DELAY_MS = 500;

export function useOnboardingRedirect(
  acquisitionIntent?: AcquisitionIntent
): UseOnboardingRedirectReturn {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('ui.oracleErrorRedirect');
  const [status, setStatus] = useState<Status>('onboarding');
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Adicionar progressError para tratar falhas
  const { userProgress, isLoading, isLoggedIn, progressError } = useUser();
  const setGuestOnboardingData = useGuestStore((state) => state.setOnboardingData);

  const checkUserOnboardingStatus = useCallback(
    (progress: { name?: string; sign?: string | null; dob?: string | Date | null } | null) => {
      return progress?.name && progress?.sign && progress?.dob;
    },
    []
  );

  const handleAuthenticatedUser = useCallback(() => {
    if (!userProgress) {
      setStatus('loading');
      return;
    }

    const hasCompletedOnboarding = checkUserOnboardingStatus(userProgress);

    if (hasCompletedOnboarding) {
      setStatus('transitioning');
      const id = setTimeout(() => {
        router.push(`/${locale}/home`);
      }, TRANSITION_DELAY_MS);
      timeoutsRef.current.push(id);
    } else {
      setStatus('onboarding');
    }
  }, [userProgress, checkUserOnboardingStatus, router, locale]);


  useEffect(() => {
    // Guests see the landing immediately — don't block on session check.
    // Only act once the session check resolves AND the user is logged in.
    if (isLoading || !isLoggedIn) {
      return;
    }

    if (progressError) {
      console.error('Redirect hook caught a user progress error:', progressError);
      setStatus('error');
      return;
    }

    handleAuthenticatedUser();
  }, [isLoggedIn, isLoading, progressError, handleAuthenticatedUser]);

  const handleMockupAccountContinue = useCallback(
    (data: OnboardingData) => {
      const zodiacSign = getZodiacSignFromDate(data.dob);
      const now = new Date().toISOString();

      setGuestOnboardingData({
        ...data,
        birthDate: data.dob,
        zodiacSign: zodiacSign ?? 'Aries',
        schemaVersion: GUEST_ONBOARDING_SCHEMA_VERSION,
        completedAt: now,
        updatedAt: now,
        source: 'onboarding',
      });
      router.push(`/${locale}/login?form=signup&redirect=/${locale}/offer`);
    },
    [locale, router, setGuestOnboardingData]
  );

  const renderComponent = useCallback(() => {
    switch (status) {
      case 'loading':
        return <Loader />;

      case 'transitioning':
        return <Loader />;

      case 'onboarding':
        return (
          <LandingExperience
            onAccountContinue={handleMockupAccountContinue}
            onExistingAccount={() => router.push(`/${locale}/login?form=signin`)}
            acquisitionIntent={acquisitionIntent}
          />
        );

      // NOVO: Renderizar um estado de erro
      case 'error':
        return (
          <div className="flex min-h-screen items-center justify-center text-center text-white">
            <div>
              <h1 className="font-bold text-2xl">{t('title')}</h1>
              <p className="mt-2 text-purple-300">
                {t('body')}
              </p>
              {/* Opcional: Adicionar um botão para tentar novamente */}
            </div>
          </div>
        );

      default:
        return <Loader />;
    }
  }, [status, handleMockupAccountContinue, router, acquisitionIntent, locale, t]);

  // Cleanup any pending timeouts on unmount
  useEffect(() => {
    return () => {
      for (const id of timeoutsRef.current) {
        clearTimeout(id);
      }
      timeoutsRef.current = [];
    };
  }, []);

  return {
    status,
    component: renderComponent(),
  };
}

// Ensure pending timeouts are cleared on unmount
// Add a tiny effect at module scope won't work; effect must be inside the hook.
